/**
 * crm-storage.ts
 *
 * Encrypted persistence layer for CRM state.
 * Production: VaultDBCrmStorage (AES-GCM, content hash verified, fail-closed)
 * Dev/Test:   InMemoryCrmStorage (explicit, never silently falls back in production)
 *
 * Architecture: the storage layer holds CrmSnapshot values — opaque encrypted
 * blobs. The CrmStore is responsible for serializing/deserializing CrmState.
 */

import type { CrmSnapshot } from './crm-contracts.js';
import { generateUUIDv7 } from '../../crypto/uuidv7.js';

// ─── Health Contract ──────────────────────────────────────────────────────────

export interface StorageHealth {
  mode: 'vaultdb' | 'native-encrypted' | 'memory';
  persistent: boolean;
  encrypted: boolean;
  warning?: string;
}

// ─── Provider contract ────────────────────────────────────────────────────────

export type CrmStorageMode = 'vaultdb' | 'memory';

export interface CrmStorageProvider {
  readonly mode: CrmStorageMode;
  saveSnapshot(snapshot: CrmSnapshot): Promise<void>;
  loadSnapshot(roomId: string): Promise<CrmSnapshot | null>;
  clearSnapshot(roomId: string): Promise<void>;
  getHealth(): StorageHealth;
}

// ─── Dev Warning Telemetry ────────────────────────────────────────────────────

let hasWarnedMemoryFallback = false;

export function reportMemoryFallback(): void {
  if (
    hasWarnedMemoryFallback ||
    (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')
  ) {
    return;
  }

  hasWarnedMemoryFallback = true;

  console.warn(
    '[DEV: Memory Persistence] CRM state will be lost on reload. VaultDB is unavailable in the current environment.',
  );
}

export function resetMemoryFallbackWarning(): void {
  hasWarnedMemoryFallback = false;
}

// ─── Shared hash utility ──────────────────────────────────────────────────────

async function sha256Hex(payload: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', payload.buffer as ArrayBuffer);
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── VaultDB production storage ───────────────────────────────────────────────

export class VaultDBCrmStorage implements CrmStorageProvider {
  readonly mode = 'vaultdb' as const;

  getHealth(): StorageHealth {
    return { mode: 'vaultdb', persistent: true, encrypted: true };
  }

  private async getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('crm_vault_storage', 1);
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('crm_snapshots')) {
          db.createObjectStore('crm_snapshots');
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async saveSnapshot(snapshot: CrmSnapshot): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('crm_snapshots', 'readwrite');
      const store = tx.objectStore('crm_snapshots');
      const req = store.put(snapshot, snapshot.roomId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async loadSnapshot(roomId: string): Promise<CrmSnapshot | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('crm_snapshots', 'readonly');
      const store = tx.objectStore('crm_snapshots');
      const req = store.get(roomId);
      req.onsuccess = async () => {
        const snapshot = req.result as CrmSnapshot | undefined;
        if (!snapshot) {
          resolve(null);
          return;
        }

        const computedHash = await sha256Hex(snapshot.encryptedPayload);
        if (computedHash !== snapshot.contentHash) {
          reject(new Error(
            `CRITICAL: CRM snapshot content hash mismatch for room "${roomId}". ` +
              `Expected "${snapshot.contentHash}", got "${computedHash}". Refusing to load.`
          ));
          return;
        }

        resolve(snapshot);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async clearSnapshot(roomId: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('crm_snapshots', 'readwrite');
      const store = tx.objectStore('crm_snapshots');
      const req = store.delete(roomId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

// ─── AES-GCM helper (used by InMemoryCrmStorage in dev mode) ─────────────────

const DEV_KEY_MATERIAL = 'crm-dev-ephemeral-key-v1';

async function deriveDevKey(): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const raw = await crypto.subtle.digest('SHA-256', enc.encode(DEV_KEY_MATERIAL).buffer as ArrayBuffer);
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export async function encryptPayload(plaintext: Uint8Array): Promise<{ iv: Uint8Array; ciphertext: Uint8Array }> {
  const key = await deriveDevKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, plaintext.buffer as ArrayBuffer),
  );
  return { iv, ciphertext };
}

export async function decryptPayload(iv: Uint8Array, ciphertext: Uint8Array): Promise<Uint8Array> {
  const key = await deriveDevKey();
  return new Uint8Array(
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, ciphertext.buffer as ArrayBuffer)
  );
}

// ─── In-memory dev storage ────────────────────────────────────────────────────

export class InMemoryCrmStorage implements CrmStorageProvider {
  readonly mode = 'memory' as const;

  private readonly store = new Map<string, CrmSnapshot>();

  getHealth(): StorageHealth {
    return {
      mode: 'memory',
      persistent: false,
      encrypted: true,
      warning: 'DEV: Memory Persistence — State will be lost on reload.',
    };
  }

  async saveSnapshot(snapshot: CrmSnapshot): Promise<void> {
    this.store.set(snapshot.roomId, snapshot);
  }

  async loadSnapshot(roomId: string): Promise<CrmSnapshot | null> {
    const snapshot = this.store.get(roomId) ?? null;
    if (!snapshot) return null;

    const computedHash = await sha256Hex(snapshot.encryptedPayload);
    if (computedHash !== snapshot.contentHash) {
      throw new Error(`DEV: CRM snapshot content hash mismatch for room "${roomId}".`);
    }
    return snapshot;
  }

  async clearSnapshot(roomId: string): Promise<void> {
    this.store.delete(roomId);
  }
}

// ─── Snapshot builder ─────────────────────────────────────────────────────────

export async function buildSnapshot(
  plaintext: string,
  roomId: string,
  revision: string,
  policyVersion: number,
): Promise<CrmSnapshot> {
  const enc = new TextEncoder();
  const { iv, ciphertext } = await encryptPayload(enc.encode(plaintext));

  const encryptedPayload = new Uint8Array(iv.length + ciphertext.length);
  encryptedPayload.set(iv, 0);
  encryptedPayload.set(ciphertext, iv.length);

  const contentHash = await sha256Hex(encryptedPayload);
  const now = Date.now();

  return {
    snapshotId: generateUUIDv7(now),
    roomId,
    schemaVersion: 2,
    revision,
    policyVersion,
    createdAt: now,
    contentHash,
    encryptedPayload,
  };
}

export async function extractSnapshot(snapshot: CrmSnapshot): Promise<string> {
  const iv = snapshot.encryptedPayload.slice(0, 12);
  const ciphertext = snapshot.encryptedPayload.slice(12);
  const plaintext = await decryptPayload(iv, ciphertext);
  return new TextDecoder().decode(plaintext);
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createCrmStorage(): CrmStorageProvider {
  if (typeof (globalThis as any).VaultDB !== 'undefined') {
    return new VaultDBCrmStorage();
  }
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    throw new Error(
      'CRITICAL: Production CRM environment requires VaultDB or native-encrypted storage. Insecure memory fallback is forbidden in production.',
    );
  }
  reportMemoryFallback();
  return new InMemoryCrmStorage();
}
